import {
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@musa.tech/design-system";
import { getMaterialColorByRank } from "@/components/dashboard/collectionsEvolutionChartColors";
import { cascadeDelayMs } from "./volumeHeatmapScale";
import { fmtMassStr } from "@/utils/formatMass";
import { fmtWeightStr } from "@/utils/formatWeight";
import {
  buildCarbonTreemapLayout,
  type CarbonBlock,
} from "./carbonTreemapLayout";
import type { MaterialCarbonBreakdown } from "@/integrations/agreements/client";

/** Cinza fora da paleta de materiais: "Outros" não é um resíduo específico. */
const AGGREGATED_COLOR = "#808080";

/**
 * Escolhe texto claro ou escuro pela luminância do fundo. Limiar em 205, não
 * 150: na paleta atual só os pastéis quase-brancos (azul névoa #c2d0ff e
 * pêssego #ffd9bc, luminância ~209 e ~225) ficam ilegíveis com texto branco —
 * até o azul principal (#8aa5ff, ~167) e o amarelo (#dfc24f, ~190) mantêm
 * texto branco.
 */
function isLightBackground(hex: string): boolean {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return false;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  // Luminância perceptual (ITU-R BT.601).
  return (r * 299 + g * 587 + b * 114) / 1000 > 205;
}

function blockColor(block: CarbonBlock, rank: number): string {
  if (block.is_aggregated || block.material_id === null) return AGGREGATED_COLOR;
  return getMaterialColorByRank(rank);
}

interface CarbonTreemapGridProps {
  entries: MaterialCarbonBreakdown[];
}

/**
 * Grade de blocos proporcionais (Figma node 345:16137).
 *
 * Não é um treemap de algoritmo: o design é um flex de duas linhas com larguras
 * proporcionais ao valor — mais simples e mais fiel que um squarified treemap,
 * que produziria outro arranjo.
 *
 * Abaixo de `md` a proporção horizontal deixa de fazer sentido (os blocos
 * teriam poucos pixels), então vira lista vertical, preservando o ranking.
 */
export const CarbonTreemapGrid = ({ entries }: CarbonTreemapGridProps) => {
  const layout = buildCarbonTreemapLayout(entries);

  if (!layout.rows.length) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Text variant="body" size="small" className="text-neutral-500">
          Nenhum dado de carbono no período
        </Text>
      </div>
    );
  }

  let blockIndex = -1;
  // Rank de cor separado do índice visual: o bloco "Outros" agregado usa
  // AGGREGATED_COLOR e não consome uma posição da paleta por representatividade.
  let colorRank = -1;

  return (
    <TooltipProvider>
      <div className="flex w-full flex-1 flex-col gap-1">
        {layout.rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={cn(
              "flex w-full flex-col gap-1 md:flex-row md:items-stretch",
              // Primeira linha com altura fixa e a segunda ocupando o resto,
              // como no Figma; em linha única o card inteiro é da linha.
              layout.rows.length > 1 && rowIndex === 0
                ? "md:h-[140px]"
                : "md:min-h-[120px] md:flex-1",
            )}
          >
            {row.map((block) => {
              blockIndex += 1;
              const isAggregated = block.is_aggregated || block.material_id === null;
              if (!isAggregated) colorRank += 1;
              const background = blockColor(block, colorRank);
              const light = isLightBackground(background);

              return (
                <Tooltip key={block.material_id ?? block.material_name}>
                  <TooltipTrigger asChild>
                    <div
                      tabIndex={0}
                      style={{
                        backgroundColor: background,
                        animationDelay: `${cascadeDelayMs(blockIndex)}ms`,
                        // `flex-grow` proporcional reparte a largura da linha.
                        // Na coluna do mobile ele é inerte: sem altura fixa no
                        // container não há espaço livre para distribuir, então
                        // cada bloco fica com a própria altura mínima.
                        flexGrow: block.widthPercent,
                        flexBasis: 0,
                      }}
                      className={cn(
                        "flex min-h-[92px] min-w-0 cursor-help flex-col justify-between overflow-hidden rounded p-4",
                        "animate-in fade-in zoom-in-95 fill-mode-both duration-300",
                        "transition-[filter] hover:brightness-95",
                        "motion-reduce:animate-none motion-reduce:transition-none",
                      )}
                    >
                      <Text
                        className={cn(
                          "truncate text-xs leading-5",
                          light ? "text-neutral-900" : "text-white",
                        )}
                      >
                        {block.material_name}
                      </Text>
                      <Text
                        className={cn(
                          "truncate text-base font-bold leading-5 tracking-[1px]",
                          light ? "text-neutral-700" : "text-neutral-200",
                        )}
                      >
                        {fmtMassStr(block.carbon_saved)}
                      </Text>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs leading-relaxed">
                    {block.material_name} — {fmtWeightStr(block.carbon_saved)} de CO₂
                    evitado
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
};
